import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  List,
  Create,
  SimpleForm,
  TextField,
  DateField,
  Resource,
  Show,
  ReferenceInput,
  ReferenceField,
  AutocompleteInput,
  Datagrid,
  SimpleShowLayout,
  Filter,
  FunctionField,
  SelectInput,
  useNotify,
  useRefresh,
  useDataProvider,
} from "react-admin";
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField as MuiTextField,
  List as MuiList,
  ListItem,
  ListItemText,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { hostname } from "../providers/api";
import { useTokenOps } from "../components/TokenContext";

// 6 subsecciones de la sección "Process by account"
const PROCESS_TYPES = [
  { key: "extraction", label: "Extraction" },
  { key: "finishing", label: "Finishing" },
  { key: "manufacturing", label: "Manufacturing" },
  { key: "packaging", label: "Packaging" },
  { key: "processing", label: "Processing" },
  { key: "transport", label: "Transport" },
  { key: "trimming", label: "Trimming" },
];

// Cache simple para evitar refetch de cuentas asignadas en re-renders del Datagrid
const accountsCache = new Map();
const cacheKey = (type, id) => `${type}:${id}`;

// Helper de logging para descubrir la forma real de las respuestas del backend.
// Imprime una sola vez por (tag, shape) para no inundar la consola.
const loggedShapes = new Set();
const logShapeOnce = (tag, payload) => {
  const sample = Array.isArray(payload)
    ? payload[0]
    : payload && payload.data
    ? Array.isArray(payload.data)
      ? payload.data[0]
      : payload.data
    : payload;
  const keys = sample && typeof sample === "object" ? Object.keys(sample).sort().join(",") : String(typeof sample);
  const sig = `${tag}::${keys}`;
  if (loggedShapes.has(sig)) return;
  loggedShapes.add(sig);
  // eslint-disable-next-line no-console
  console.log(`[ProcessByAccount][${tag}] keys=`, keys, "| sample=", sample, "| full=", payload);
};

// Wrapper de los endpoints anidados de asignación
const accountsApi = (type, accessToken) => {
  const headers = { Authorization: `Bearer ${accessToken}` };
  const jsonHeaders = { ...headers, "Content-Type": "application/json" };

  return {
    list: async (originId) => {
      const key = cacheKey(type, originId);
      if (accountsCache.has(key)) return accountsCache.get(key);
      const url = `${hostname}/processOrigin/${type}/${originId}/accounts`;
      // eslint-disable-next-line no-console
      console.log(`[ProcessByAccount][GET accounts] ${url}`);
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      logShapeOnce(`accounts:${type}`, json);
      const data = Array.isArray(json) ? json : json.data || [];
      accountsCache.set(key, data);
      return data;
    },
    invalidate: (originId) => accountsCache.delete(cacheKey(type, originId)),
    add: async (originId, accountId) => {
      const res = await fetch(
        `${hostname}/processOrigin/${type}/${originId}/accounts`,
        {
          method: "POST",
          headers: jsonHeaders,
          body: JSON.stringify({ accountId }),
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      accountsCache.delete(cacheKey(type, originId));
      const j = await res.json().catch(() => ({}));
      // eslint-disable-next-line no-console
      console.log(`[ProcessByAccount][POST account] type=${type} originId=${originId} response=`, j);
      return j;
    },
    remove: async (originId, accountId) => {
      const res = await fetch(
        `${hostname}/processOrigin/${type}/${originId}/accounts/${accountId}`,
        { method: "DELETE", headers }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      accountsCache.delete(cacheKey(type, originId));
    },
    promoteToSpecific: async (originId) => {
      const res = await fetch(
        `${hostname}/processOrigin/${type}/${originId}/promote-to-specific`,
        { method: "PATCH", headers }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      accountsCache.delete(cacheKey(type, originId));
      return res.json().catch(() => ({}));
    },
  };
};

// "All" es una opción real (no el empty del SelectInput) para que se muestre
// el label en el selector. El dataProvider traduce type="all" a type IN [Custom, Specific].
const TYPE_CHOICES = [
  { id: "all", name: "All" },
  { id: "Custom", name: "Custom" },
  { id: "Specific", name: "Specific" },
];

// Nota: NO se puede filtrar por accountId acá. processOrigin/<type> no tiene
// columna accountId (las asignaciones viven en una sub-tabla). Backend devuelve 422.
// La búsqueda por cuenta solo tiene sentido a nivel de asignación, no de processOrigin.
const ListFilters = (props) => (
  <Filter {...props}>
    <SelectInput
      source="type"
      label="Type"
      choices={TYPE_CHOICES}
      alwaysOn
      // FilterFormInput fuerza allowEmpty=true por defecto (agrega una opción en
      // blanco). Lo desactivamos explícitamente: "All" ya cumple ese rol.
      allowEmpty={false}
    />
  </Filter>
);

// Celda que muestra las cuentas asignadas a un processOrigin. Reusa el cache
// de accountsApi, así que la mayoría de los renders no dispara fetch.
const AccountNameCell = ({ record, type }) => {
  const tokenOps = useTokenOps();
  const token = tokenOps?.account?.accessToken;
  const [names, setNames] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!record?.id || !token) return;
    // Solo Custom/Specific tienen cuentas asignadas
    if (record.type !== "Custom" && record.type !== "Specific") {
      setNames([]);
      return;
    }
    accountsApi(type, token)
      .list(record.id)
      .then((data) => {
        if (cancelled) return;
        setNames(
          (data || []).map(
            (a) =>
              (a.account && a.account.name) ||
              a.name ||
              a.accountName ||
              a.accountId ||
              a.id
          )
        );
      })
      .catch(() => {
        if (!cancelled) setNames([]);
      });
    return () => {
      cancelled = true;
    };
  }, [record?.id, record?.type, token, type]);

  if (names === null) return <CircularProgress size={12} />;
  if (names.length === 0) return <span style={{ color: "#999" }}>—</span>;
  return <span>{names.join(", ")}</span>;
};

const buildList = (type) => (props) => (
  <List
    {...props}
    resource={`processByAccount/${type}`}
    filters={<ListFilters />}
    filterDefaultValues={type === "trimming" ? { type: "all" } : { type: "Custom" }}
    bulkActionButtons={false}
    sort={{ field: "id", order: "ASC" }}
    // empty={false}: evita el empty-state por defecto de react-admin (que oculta
    // la barra de filtros). Así, aunque no haya filas, sigue visible el filtro Type
    // para poder cambiar entre All/Custom/Specific.
    empty={false}
  >
    <Datagrid rowClick="show">
      <TextField source="id" />
      {/* Process name: el row ya trae el objeto del proceso embebido bajo la key <type> */}
      <TextField source={`${type}.name`} label="Process name" />
      <ReferenceField
        label="Origin"
        source="originId"
        reference="origins"
        link={false}
      >
        <TextField source="name" />
      </ReferenceField>
      {/* Database context: viene embebido como objeto en cada row */}
      <TextField source="databaseContext.name" label="Database context" />
      <TextField source="energyType" label="Energy type" />
      <TextField source="type" label="Type" />
      <FunctionField
        label="Account name"
        render={(record) => <AccountNameCell record={record} type={type} />}
      />
    </Datagrid>
  </List>
);

// Panel embebido en el Show para gestionar las cuentas asignadas
const AccountsManager = ({ record, type }) => {
  const tokenOps = useTokenOps();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const token = tokenOps?.account?.accessToken;
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [acting, setActing] = useState(false);
  const debounceRef = useRef(null);

  const isCustom = record?.type === "Custom";
  const isSpecific = record?.type === "Specific";
  const isAssignable = isCustom || isSpecific;

  const reload = useCallback(async () => {
    if (!record?.id || !token) return;
    setLoading(true);
    try {
      accountsApi(type, token).invalidate(record.id);
      const data = await accountsApi(type, token).list(record.id);
      setAccounts(data);
    } catch (e) {
      notify(`Error cargando cuentas: ${e.message}`, "warning");
    }
    setLoading(false);
  }, [record?.id, token, type, notify]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Búsqueda de cuentas a través del dataProvider de react-admin para reusar el
  // composeFilter del proyecto (operador $like + manejo de wildcards en backend).
  // Mismo patrón que filterToQuery en los AutocompleteInput de otras Lists.
  const fetchAccounts = useCallback(
    async (query) => {
      const trimmed = (query || "").trim();
      const { data } = await dataProvider.getList("accounts", {
        pagination: { page: 1, perPage: 50 },
        sort: { field: "name", order: "ASC" },
        filter: trimmed.length > 0 ? { name: trimmed } : {},
      });
      return data || [];
    },
    [dataProvider]
  );

  // Dispara búsqueda con debounce cuando cambia el query y el diálogo está abierto
  useEffect(() => {
    if (!dialogOpen) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const list = await fetchAccounts(searchQuery);
        setSearchResults(list);
      } catch (e) {
        notify(`Error buscando cuentas: ${e.message}`, "warning");
        setSearchResults([]);
      }
      setSearching(false);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery, dialogOpen, fetchAccounts, notify]);

  const openAddDialog = () => {
    setSearchQuery("");
    setSelectedAccount(null);
    setSearchResults([]);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSearchQuery("");
    setSelectedAccount(null);
  };

  const handleAssign = async () => {
    if (!selectedAccount) return;
    setActing(true);
    try {
      await accountsApi(type, token).add(record.id, selectedAccount.id);
      notify("Cuenta asignada", "info");
      closeDialog();
      reload();
    } catch (e) {
      notify(`Asignación falló: ${e.message}`, "warning");
    }
    setActing(false);
  };

  const handleRemove = async (accountId) => {
    setActing(true);
    try {
      await accountsApi(type, token).remove(record.id, accountId);
      notify("Cuenta eliminada", "info");
      reload();
    } catch (e) {
      notify(`Eliminación falló: ${e.message}`, "warning");
    }
    setActing(false);
  };

  const handlePromote = async () => {
    setActing(true);
    try {
      await accountsApi(type, token).promoteToSpecific(record.id);
      notify("Promovido a Specific", "info");
      refresh();
    } catch (e) {
      notify(`Promote falló: ${e.message}`, "warning");
    }
    setActing(false);
  };

  if (!isAssignable) {
    return (
      <div style={{ padding: 16, color: "#666" }}>
        Este processOrigin es <strong>{record?.type || "—"}</strong> y no admite
        asignación a cuentas. Solo los tipos <em>Custom</em> y <em>Specific</em>
        se asignan a cuentas.
      </div>
    );
  }

  // Excluye las ya asignadas
  const availableAccounts = searchResults.filter(
    (a) =>
      !accounts.some(
        (aa) =>
          (aa.accountId || (aa.account && aa.account.id) || aa.id) === a.id
      )
  );

  return (
    <div
      style={{
        padding: 16,
        border: "1px solid #eee",
        borderRadius: 4,
        marginTop: 16,
      }}
    >
      <h3 style={{ margin: "0 0 12px" }}>Assigned accounts</h3>

      {loading ? (
        <CircularProgress size={16} />
      ) : (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
            marginBottom: 12,
          }}
        >
          {accounts.length === 0 && (
            <span style={{ color: "#999" }}>Sin cuentas asignadas</span>
          )}
          {accounts.map((a) => {
            const accId = a.accountId || (a.account && a.account.id) || a.id;
            const accName =
              (a.account && a.account.name) || a.name || a.accountName || "";
            return (
              <Chip
                key={accId}
                label={`${accName} (${accId})`}
                onDelete={() => handleRemove(accId)}
                disabled={acting}
              />
            );
          })}
        </div>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          disabled={acting || (isCustom && accounts.length >= 1)}
          onClick={openAddDialog}
        >
          Assign account
        </Button>
        {isCustom && (
          <Button
            variant="outlined"
            onClick={handlePromote}
            disabled={acting}
          >
            Promote to Specific
          </Button>
        )}
      </div>

      {isCustom && accounts.length >= 1 && (
        <p style={{ color: "#999", fontSize: 12, marginTop: 8 }}>
          Tipo <em>Custom</em> admite una sola cuenta. Promote a{" "}
          <em>Specific</em> para asignar más.
        </p>
      )}

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Asignar cuenta</DialogTitle>
        <DialogContent>
          <MuiTextField
            label="Buscar cuenta por nombre"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setSelectedAccount(null);
            }}
            fullWidth
            autoFocus
            variant="outlined"
            margin="dense"
          />
          <div
            style={{
              marginTop: 8,
              border: "1px solid #e0e0e0",
              borderRadius: 4,
              maxHeight: 280,
              overflowY: "auto",
            }}
          >
            {searching ? (
              <div style={{ padding: 16, textAlign: "center" }}>
                <CircularProgress size={20} />
              </div>
            ) : availableAccounts.length === 0 ? (
              <div style={{ padding: 16, color: "#999", textAlign: "center" }}>
                {searchQuery ? "Sin resultados" : "Escribí para buscar…"}
              </div>
            ) : (
              <MuiList dense disablePadding>
                {availableAccounts.map((a) => {
                  const isSelected =
                    selectedAccount && selectedAccount.id === a.id;
                  return (
                    <ListItem
                      key={a.id}
                      button
                      selected={isSelected}
                      onClick={() => setSelectedAccount(a)}
                    >
                      <ListItemText
                        primary={a.name}
                        secondary={a.id}
                      />
                    </ListItem>
                  );
                })}
              </MuiList>
            )}
          </div>
          {selectedAccount && (
            <div style={{ marginTop: 8, fontSize: 13, color: "#444" }}>
              Seleccionada: <strong>{selectedAccount.name}</strong>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancelar</Button>
          <Button
            onClick={handleAssign}
            color="primary"
            disabled={!selectedAccount || acting}
          >
            Asignar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const buildShow = (type) => (props) => (
  <Show {...props} resource={`processByAccount/${type}`}>
    <SimpleShowLayout>
      <TextField source="id" />
      <TextField source={`${type}.name`} label="Process name" />
      <ReferenceField
        label="Origin"
        source="originId"
        reference="origins"
        link={false}
      >
        <TextField source="name" />
      </ReferenceField>
      <TextField source="databaseContext.name" label="Database context" />
      <TextField source="energyType" label="Energy type" />
      <TextField source="type" label="Type" />
      <DateField source="createdAt" label="Creation date" />
      <DateField source="updatedAt" label="Update date" />
      <FunctionField
        addLabel={false}
        render={(record) => (
          <AccountsManager record={record} type={type} />
        )}
      />
    </SimpleShowLayout>
  </Show>
);

const buildCreate = (type) => (props) => (
  <Create
    {...props}
    resource={`processByAccount/${type}`}
    title={`Create ${type} assignment`}
  >
    <SimpleForm redirect="list">
      <ReferenceInput
        label="Process origin"
        source="processOriginId"
        reference={`processOrigin/${type}`}
        perPage={25}
        filterToQuery={(searchText) => ({ [`${type}.name`]: searchText })}
        filter={{ type: ["Custom", "Specific"] }}
      >
        <AutocompleteInput
          optionText={(r) => {
            if (!r) return "";
            const processName = (r[type] && r[type].name) || "(sin nombre)";
            const dbCtx = (r.databaseContext && r.databaseContext.name) || "";
            const t = r.type || "";
            return `${processName}${dbCtx ? ` · ${dbCtx}` : ""}${t ? ` · ${t}` : ""}`;
          }}
          options={{
            suggestionsContainerProps: {
              style: { maxWidth: 520, zIndex: 1500 },
            },
          }}
        />
      </ReferenceInput>
      <ReferenceInput
        label="Account"
        source="accountId"
        reference="accounts"
        filterToQuery={(searchText) => ({ name: searchText })}
      >
        <AutocompleteInput optionText="name" />
      </ReferenceInput>
    </SimpleForm>
  </Create>
);

const makeResource = ({ key, label }) => () => (
  <Resource
    name={`processByAccount/${key}`}
    options={{ label, menuGroup: "Process by account" }}
    list={buildList(key)}
    create={buildCreate(key)}
    show={buildShow(key)}
  />
);

export const PROCESS_BY_ACCOUNT_RESOURCE_NAMES = PROCESS_TYPES.map(
  (t) => `processByAccount/${t.key}`
);

export const accountExtractionOriginResource = makeResource(PROCESS_TYPES[0]);
export const accountFinishingOriginResource = makeResource(PROCESS_TYPES[1]);
export const accountManufacturingOriginResource = makeResource(PROCESS_TYPES[2]);
export const accountPackagingOriginResource = makeResource(PROCESS_TYPES[3]);
export const accountProcessingOriginResource = makeResource(PROCESS_TYPES[4]);
export const accountTransportOriginResource = makeResource(PROCESS_TYPES[5]);
export const accountTrimmingOriginResource = makeResource(PROCESS_TYPES[6]);
